/**
 * 🎓 ADMIN - GOUVERNANCE PÉDAGOGIQUE
 * ==================================
 * 
 * Dashboard admin pour la gestion Qualiopi/CPF:
 * - Diagnostics pédagogiques
 * - Registre Qualiopi
 * - Exports & preuves
 * 
 * Qualiopi-ready
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { 
  ArrowLeft, Brain, FileText, Download, Users, Target,
  Award, TrendingUp, CheckCircle2, AlertCircle, BookOpen,
  Calendar, BarChart3, Shield, RefreshCw, FileDown, MessageSquare, Star
} from 'lucide-react';
import { getBackendUrl } from '../../config/environment';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const AdminPedagogicalGovernance = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [divergencesData, setDivergencesData] = useState(null);
  const [diagnostics, setDiagnostics] = useState([]);
  const [auditPackage, setAuditPackage] = useState(null);
  const [feedbacksData, setFeedbacksData] = useState(null);

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/login');
      return;
    }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const [dashRes, divergRes, diagRes, auditRes, feedbacksRes] = await Promise.all([
        axios.get(`${API}/admin/pedagogical-governance/dashboard`),
        axios.get(`${API}/admin/pedagogical-governance/divergences-dashboard`),
        axios.get(`${API}/admin/pedagogical-governance/diagnostics`),
        axios.get(`${API}/admin/pedagogical-governance/export/audit-package`),
        axios.get(`${API}/feedback/admin/all`)
      ]);
      
      setDashboardData(dashRes.data);
      setDivergencesData(divergRes.data);
      setDiagnostics(diagRes.data.diagnostics || []);
      setAuditPackage(auditRes.data);
      setFeedbacksData(feedbacksRes.data);
    } catch (err) {
      console.error('Erreur chargement données:', err);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async (type) => {
    try {
      const endpoints = {
        diagnostics: '/admin/pedagogical-governance/export/diagnostics/csv',
        objectives: '/admin/pedagogical-governance/export/objectives/csv',
        justifications: '/admin/pedagogical-governance/export/justifications/csv'
      };
      
      const response = await axios.get(`${API}${endpoints[type]}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`Export ${type} téléchargé`);
    } catch (err) {
      console.error('Erreur export:', err);
      toast.error('Erreur lors de l\'export');
    }
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'divergences', label: 'Divergences', icon: AlertCircle },
    { id: 'feedbacks', label: 'Feedbacks Modules', icon: MessageSquare },
    { id: 'diagnostics', label: 'Diagnostics', icon: Brain },
    { id: 'exports', label: 'Exports & Preuves', icon: FileDown }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="w-12 h-12 border-4 border-[#3B1E73] border-t-transparent rounded-full"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/admin')}
                className="gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Retour Admin
              </Button>
              <div className="h-6 w-px bg-gray-200" />
              <div className="flex items-center gap-2">
                <div className="p-2 bg-gradient-to-br from-[#3B1E73] to-[#8B5CF6] rounded-lg">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-[#2B1854]">Gouvernance Pédagogique</h1>
                  <p className="text-xs text-gray-500">Qualiopi & CPF</p>
                </div>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={loadData}
              className="gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Actualiser
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 bg-white rounded-lg p-1 shadow-sm border">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium text-sm transition-all ${
                activeTab === tab.id
                  ? 'bg-[#3B1E73] text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        {activeTab === 'dashboard' && (
          <DashboardTab data={dashboardData} />
        )}
        
        {activeTab === 'divergences' && (
          <DivergencesTab data={divergencesData} onRefresh={loadData} />
        )}
        
        {activeTab === 'feedbacks' && (
          <FeedbacksTab data={feedbacksData} onRefresh={loadData} />
        )}
        
        {activeTab === 'diagnostics' && (
          <DiagnosticsTab diagnostics={diagnostics} stats={dashboardData?.stats} />
        )}
        
        {activeTab === 'exports' && (
          <ExportsTab auditPackage={auditPackage} onExport={handleExportCSV} />
        )}
      </div>
    </div>
  );
};

/**
 * Dashboard Tab
 */
const DashboardTab = ({ data }) => {
  if (!data) return null;
  
  const { stats, qualiopi_compliance, governance } = data;

  const statCards = [
    { label: 'Utilisateurs', value: stats.total_users, icon: Users, color: 'blue' },
    { label: 'Sessions', value: stats.total_sessions, icon: BookOpen, color: 'purple' },
    { label: 'Objectifs validés', value: stats.total_objectives_validated, icon: Target, color: 'green' },
    { label: 'Modules validés', value: stats.total_modules_validated, icon: Award, color: 'amber' },
    { label: 'Certificats', value: stats.total_certificates_generated, icon: FileText, color: 'emerald' },
    { label: 'Feedbacks', value: stats.total_feedbacks, icon: TrendingUp, color: 'pink' }
  ];

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {statCards.map((stat, idx) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.05 }}
          >
            <Card className="border-0 shadow-sm bg-white hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className={`w-10 h-10 rounded-lg bg-${stat.color}-100 flex items-center justify-center mb-3`}>
                  <stat.icon className={`w-5 h-5 text-${stat.color}-600`} />
                </div>
                <p className="text-2xl font-bold text-[#2B1854]">{stat.value}</p>
                <p className="text-xs text-gray-500">{stat.label}</p>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Conformité Qualiopi */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#3B1E73]" />
            Conformité Qualiopi
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(qualiopi_compliance).map(([key, value]) => (
              <div 
                key={key}
                className={`p-4 rounded-lg border ${
                  value ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  {value ? (
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-500" />
                  )}
                  <span className={`font-medium ${value ? 'text-green-700' : 'text-red-700'}`}>
                    {value ? 'Conforme' : 'Non conforme'}
                  </span>
                </div>
                <p className="text-sm text-gray-600">
                  {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Gouvernance */}
      <Card className="border-0 shadow-sm bg-gradient-to-br from-[#3B1E73]/5 to-[#8B5CF6]/5">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-[#3B1E73] rounded-xl">
              <Brain className="w-8 h-8 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-[#2B1854] text-lg">Gouvernance Owen</h3>
              <p className="text-sm text-gray-600">
                Autorité pédagogique : <span className="font-semibold text-[#3B1E73]">{governance.authority}</span>
              </p>
              <p className="text-sm text-gray-600">
                Contrôle utilisateur : <span className="font-semibold text-red-600">{governance.user_control ? 'Oui' : 'Désactivé'}</span>
              </p>
              <p className="text-sm text-gray-600">
                Sources de données : <span className="font-semibold">{governance.data_sources.join(', ')}</span>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

/**
 * 🔍 Divergences Tab - Dashboard des divergences et audit trail
 * Implémenté suite à l'Audit 360° (30/12/2024)
 */
const DivergencesTab = ({ data, onRefresh }) => {
  const [fixingDivergence, setFixingDivergence] = useState(null);
  
  if (!data) return (
    <div className="flex items-center justify-center h-64">
      <p className="text-gray-500">Chargement des données de divergences...</p>
    </div>
  );

  const { global_stats, modules_breakdown, divergences, audit_trail, compliance } = data;

  const handleFixDivergence = async (divergence) => {
    try {
      setFixingDivergence(divergence.objective_id);
      
      const fixType = divergence.type === 'P0_100_SANS_VALIDATION' 
        ? 'sync_validation' 
        : 'sync_progress';
      
      await axios.post(
        `${API}/admin/pedagogical-governance/fix-divergence`,
        null,
        { params: { user_id: divergence.user_id, objective_id: divergence.objective_id, fix_type: fixType } }
      );
      
      toast.success(`Divergence corrigée: ${divergence.objective_id}`);
      onRefresh();
    } catch (err) {
      console.error('Erreur correction divergence:', err);
      toast.error('Erreur lors de la correction');
    } finally {
      setFixingDivergence(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* 📊 Stats globales modules */}
      <div className="grid md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm bg-gradient-to-br from-blue-50 to-blue-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500 rounded-lg">
                <Target className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-700">{global_stats?.modules_validation_percentage || 0}%</p>
                <p className="text-xs text-blue-600">Modules validés</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm bg-gradient-to-br from-green-50 to-green-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-700">{global_stats?.modules_with_completions || 0}/{global_stats?.total_modules_defined || 20}</p>
                <p className="text-xs text-green-600">Modules avec complétion</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm bg-gradient-to-br from-purple-50 to-purple-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500 rounded-lg">
                <Users className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-700">{global_stats?.total_active_users || 0}</p>
                <p className="text-xs text-purple-600">Utilisateurs actifs</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className={`border-0 shadow-sm ${divergences?.count > 0 ? 'bg-gradient-to-br from-red-50 to-red-100' : 'bg-gradient-to-br from-emerald-50 to-emerald-100'}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${divergences?.count > 0 ? 'bg-red-500' : 'bg-emerald-500'}`}>
                <AlertCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className={`text-2xl font-bold ${divergences?.count > 0 ? 'text-red-700' : 'text-emerald-700'}`}>{divergences?.count || 0}</p>
                <p className={`text-xs ${divergences?.count > 0 ? 'text-red-600' : 'text-emerald-600'}`}>Divergences détectées</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 📈 Progression par module */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="border-b">
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#3B1E73]" />
            Taux de validation par module
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
            {modules_breakdown?.map((module) => (
              <div key={module.module_id} className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-600">Module {module.module_id}</span>
                  <Badge variant={module.users_module_complete > 0 ? "default" : "secondary"} className="text-xs">
                    {module.users_module_complete > 0 ? `${module.users_module_complete} complétés` : 'En cours'}
                  </Badge>
                </div>
                <p className="text-sm font-medium text-gray-800 truncate mb-2">{module.module_title}</p>
                <div className="space-y-1">
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>{module.objectives_validated} obj. validés</span>
                    <span>{module.validation_rate_percent}%</span>
                  </div>
                  <Progress value={module.validation_rate_percent} className="h-2" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ⚠️ Divergences détectées */}
      {divergences?.count > 0 && (
        <Card className="border-0 shadow-lg border-l-4 border-l-red-500">
          <CardHeader className="border-b bg-red-50">
            <CardTitle className="flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5" />
              Divergences P0 à corriger ({divergences.severity_p0})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="space-y-3">
              {divergences.details?.map((div, idx) => (
                <div key={idx} className="p-3 bg-red-50 rounded-lg flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <Badge variant="destructive" className="text-xs">{div.severity}</Badge>
                      <span className="text-sm font-medium">{div.type}</span>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">{div.description}</p>
                    <p className="text-xs text-gray-500">User: {div.user_id} | Obj: {div.objective_id}</p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleFixDivergence(div)}
                    disabled={fixingDivergence === div.objective_id}
                    className="text-red-600 border-red-300 hover:bg-red-50"
                  >
                    {fixingDivergence === div.objective_id ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      'Corriger'
                    )}
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ✅ Conformité */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="border-b">
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#3B1E73]" />
            Conformité Audit 360°
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="grid md:grid-cols-3 gap-4">
            <div className={`p-4 rounded-lg ${compliance?.mongodb_source_unique ? 'bg-green-50' : 'bg-red-50'}`}>
              <div className="flex items-center gap-2">
                {compliance?.mongodb_source_unique ? (
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-600" />
                )}
                <span className="font-medium">MongoDB Source Unique</span>
              </div>
              <p className="text-xs text-gray-600 mt-1">Source de vérité centralisée</p>
            </div>
            
            <div className={`p-4 rounded-lg ${compliance?.zero_fallback ? 'bg-green-50' : 'bg-amber-50'}`}>
              <div className="flex items-center gap-2">
                {compliance?.zero_fallback ? (
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                )}
                <span className="font-medium">Zéro Fallback</span>
              </div>
              <p className="text-xs text-gray-600 mt-1">Aucune divergence détectée</p>
            </div>
            
            <div className={`p-4 rounded-lg ${compliance?.qualiopi_ready ? 'bg-green-50' : 'bg-red-50'}`}>
              <div className="flex items-center gap-2">
                {compliance?.qualiopi_ready ? (
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-600" />
                )}
                <span className="font-medium">Qualiopi Ready</span>
              </div>
              <p className="text-xs text-gray-600 mt-1">Aucun P0 bloquant</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 📋 Audit Trail */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="border-b">
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#3B1E73]" />
            Audit Trail
            <Badge variant="secondary" className="ml-2">
              Couverture: {audit_trail?.coverage_percentage || 0}%
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="space-y-2 mb-4">
            <p className="text-sm text-gray-600">
              <span className="font-medium">{audit_trail?.documents_with_audit || 0}</span> documents avec audit trail sur <span className="font-medium">{audit_trail?.total_documents || 0}</span> total
            </p>
          </div>
          
          {audit_trail?.recent_entries?.length > 0 && (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              <p className="text-xs font-medium text-gray-500 uppercase">Dernières entrées</p>
              {audit_trail.recent_entries.map((entry, idx) => (
                <div key={idx} className="p-2 bg-gray-50 rounded text-xs">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{entry.action}</span>
                    <span className="text-gray-400">{new Date(entry.timestamp).toLocaleString('fr-FR')}</span>
                  </div>
                  <div className="text-gray-500 mt-1">
                    {entry.objective_id} | {entry.previous_progress}% → {entry.new_progress}%
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

/**
 * Diagnostics Tab
 */
const DiagnosticsTab = ({ diagnostics, stats }) => {
  return (
    <div className="space-y-6">
      {/* Stats diagnostics */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Brain className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#2B1854]">{stats?.entry_diagnostics || 0}</p>
                <p className="text-sm text-gray-500">Diagnostics d&apos;entrée</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Award className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#2B1854]">{stats?.exit_diagnostics || 0}</p>
                <p className="text-sm text-gray-500">Diagnostics de sortie</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <TrendingUp className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#2B1854]">
                  {/* 🔒 P0 FIX: Éviter NaN - vérifier que le diviseur > 0 */}
                  {(() => {
                    const diagsWithDelta = diagnostics.filter(d => d.delta_progression != null && d.delta_progression !== undefined);
                    if (diagsWithDelta.length === 0) return 'N/A';
                    const avgDelta = Math.round(diagsWithDelta.reduce((sum, d) => sum + (d.delta_progression || 0), 0) / diagsWithDelta.length);
                    return avgDelta >= 0 ? `+${avgDelta}%` : `${avgDelta}%`;
                  })()}
                </p>
                <p className="text-sm text-gray-500">Progression moyenne</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Liste des diagnostics */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle>Historique des diagnostics</CardTitle>
        </CardHeader>
        <CardContent>
          {diagnostics.length === 0 ? (
            <p className="text-center text-gray-500 py-8">Aucun diagnostic enregistré</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Utilisateur</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Type</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Score</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {diagnostics.slice(0, 20).map((diag, idx) => (
                    <tr key={diag.diagnostic_id || idx} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm">{diag.user_id}</td>
                      <td className="py-3 px-4">
                        <Badge className={diag.diagnostic_type === 'entry' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}>
                          {diag.diagnostic_type === 'entry' ? 'Entrée' : 'Sortie'}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-sm font-medium">
                        {diag.percentage || diag.final_score_estimate}%
                        {diag.delta_progression && (
                          <span className="text-green-600 ml-2">(+{diag.delta_progression}%)</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-500">
                        {new Date(diag.created_at).toLocaleDateString('fr-FR')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

/**
 * Exports Tab - Version Qualiopi Audit-Ready
 * Structure claire par blocs logiques avec explications
 */
const ExportsTab = ({ auditPackage, onExport }) => {
  if (!auditPackage) return null;

  // Regrouper les exports par catégorie Qualiopi
  const exportCategories = [
    {
      id: 'positioning',
      title: '📋 Positionnement & Diagnostics',
      description: 'Évaluations initiales et finales des apprenants',
      qualiopi: 'Indicateur 11 - Adaptation au profil du bénéficiaire',
      usage: 'Prouver l\'analyse des besoins et l\'adaptation du parcours',
      color: 'blue',
      icon: Brain,
      exports: auditPackage.exports?.filter(e => e.name.toLowerCase().includes('diagnostic'))
    },
    {
      id: 'progress',
      title: '🎯 Objectifs & Compétences Validés',
      description: 'Preuves d\'acquisition des compétences par Owen',
      qualiopi: 'Preuves d\'acquisition - Traçabilité pédagogique',
      usage: 'Démontrer l\'atteinte des objectifs pédagogiques',
      color: 'green',
      icon: Target,
      exports: auditPackage.exports?.filter(e => 
        e.name.toLowerCase().includes('objectif') || 
        e.name.toLowerCase().includes('justification')
      )
    },
    {
      id: 'feedback',
      title: '💬 Feedbacks & Amélioration Continue',
      description: 'Retours apprenants et registre Qualiopi',
      qualiopi: 'Indicateur 32 - Amélioration continue',
      usage: 'Montrer la prise en compte des retours apprenants',
      color: 'purple',
      icon: MessageSquare,
      exports: auditPackage.exports?.filter(e => 
        e.name.toLowerCase().includes('feedback') || 
        e.name.toLowerCase().includes('registre')
      )
    },
    {
      id: 'certificates',
      title: '📜 Attestations & Certificats',
      description: 'Documents officiels délivrés aux apprenants',
      qualiopi: 'Traçabilité - Documents opposables',
      usage: 'Fournir les preuves de formation certifiante',
      color: 'amber',
      icon: Award,
      exports: auditPackage.exports?.filter(e => 
        e.name.toLowerCase().includes('attestation') || 
        e.name.toLowerCase().includes('certificat')
      )
    }
  ];

  // Colorations pour les badges
  const colorClasses = {
    blue: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200', icon: 'text-blue-600' },
    green: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200', icon: 'text-green-600' },
    purple: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200', icon: 'text-purple-600' },
    amber: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200', icon: 'text-amber-600' }
  };

  return (
    <div className="space-y-8">
      {/* Header Package Audit */}
      <Card className="border-2 border-[#3B1E73]/20 shadow-lg bg-gradient-to-br from-white via-[#3B1E73]/5 to-[#FFD86F]/10">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-4 bg-gradient-to-br from-[#3B1E73] to-[#5B2E93] rounded-2xl shadow-lg">
                <Shield className="w-10 h-10 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-[#2B1854]">Package Audit Qualiopi</h2>
                <p className="text-gray-600 mt-1">
                  Toutes les preuves pédagogiques pour votre certification
                </p>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-center">
                <p className="text-4xl font-bold text-[#3B1E73]">{auditPackage.total_data_points}</p>
                <p className="text-xs text-gray-500 font-medium">points de données</p>
              </div>
              <div className="h-12 w-px bg-gray-200" />
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">✓</p>
                <p className="text-xs text-gray-500 font-medium">Audit-ready</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Info gouvernance Owen */}
      <Card className="border-0 shadow-sm bg-gradient-to-r from-[#3B1E73]/5 to-transparent">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <Brain className="w-5 h-5 text-[#3B1E73]" />
            <p className="text-sm text-gray-700">
              <span className="font-semibold text-[#3B1E73]">Gouvernance Owen</span> — 
              Toutes les validations sont effectuées exclusivement par l&apos;IA pédagogique. 
              Aucune modification manuelle possible. Données horodatées et opposables.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Blocs par catégorie */}
      {exportCategories.map((category, catIdx) => (
        <motion.div
          key={category.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: catIdx * 0.1 }}
        >
          <Card className={`border-2 ${colorClasses[category.color].border} shadow-sm overflow-hidden`}>
            {/* Header de catégorie */}
            <div className={`${colorClasses[category.color].bg} px-6 py-4 border-b ${colorClasses[category.color].border}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 bg-white rounded-lg shadow-sm`}>
                    <category.icon className={`w-6 h-6 ${colorClasses[category.color].icon}`} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#2B1854]">{category.title}</h3>
                    <p className="text-sm text-gray-600">{category.description}</p>
                  </div>
                </div>
                <Badge className={`${colorClasses[category.color].bg} ${colorClasses[category.color].text} border ${colorClasses[category.color].border}`}>
                  {category.qualiopi}
                </Badge>
              </div>
            </div>

            <CardContent className="p-6">
              {/* Usage explicatif */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-100">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-gray-700">Usage pour l&apos;audit</p>
                    <p className="text-sm text-gray-600">{category.usage}</p>
                  </div>
                </div>
              </div>

              {/* Exports de cette catégorie */}
              {category.exports && category.exports.length > 0 ? (
                <div className="grid md:grid-cols-2 gap-4">
                  {category.exports.map((exp, idx) => (
                    <div 
                      key={exp.name}
                      className="p-4 bg-white border border-gray-200 rounded-xl hover:shadow-md transition-all group"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h4 className="font-semibold text-[#2B1854] group-hover:text-[#3B1E73] transition-colors">
                            {exp.name}
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">
                            Indicateur Qualiopi: <span className="font-medium">{exp.qualiopi_indicator}</span>
                          </p>
                        </div>
                        <div className="text-right">
                          <span className="text-2xl font-bold text-[#3B1E73]">{exp.count}</span>
                          <p className="text-xs text-gray-400">entrées</p>
                        </div>
                      </div>

                      {/* Détail colonnes CSV si applicable */}
                      {exp.endpoint.includes('/csv') && (
                        <div className="mb-3 p-2 bg-gray-50 rounded text-xs text-gray-600">
                          <span className="font-medium">Colonnes CSV: </span>
                          {exp.name.includes('Diagnostic') && 'ID, Utilisateur, Type, Score, Pourcentage, Δ Progression, Date'}
                          {exp.name.includes('Objectif') && 'Utilisateur, Module, Objectif, Progression, Validé Owen, Date, Preuves'}
                          {exp.name.includes('Justification') && 'ID, Utilisateur, Module, Objectif, Titre, Justification, Niveau, Preuves, Date'}
                        </div>
                      )}

                      {/* Bouton export */}
                      {exp.endpoint.includes('/csv') && (
                        <Button
                          size="sm"
                          className="w-full gap-2 bg-[#3B1E73] hover:bg-[#2B1854] text-white"
                          onClick={() => {
                            const type = exp.endpoint.includes('diagnostics') ? 'diagnostics' 
                              : exp.endpoint.includes('objectives') ? 'objectives' 
                              : 'justifications';
                            onExport(type);
                          }}
                        >
                          <Download className="w-4 h-4" />
                          Télécharger CSV
                        </Button>
                      )}

                      {!exp.endpoint.includes('/csv') && (
                        <p className="text-xs text-gray-400 italic">
                          Accessible via l&apos;onglet dédié ou l&apos;API
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-400 py-4">
                  Aucun export disponible dans cette catégorie
                </p>
              )}
            </CardContent>
          </Card>
        </motion.div>
      ))}

      {/* Checklist Auditeur */}
      <Card className="border-2 border-green-200 shadow-sm bg-green-50/50">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-green-800">
            <CheckCircle2 className="w-5 h-5" />
            Checklist Auditeur Qualiopi
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { label: 'Diagnostics d\'entrée disponibles', check: (auditPackage.exports?.find(e => e.name.includes('Diagnostic'))?.count || 0) > 0 },
              { label: 'Objectifs validés tracés', check: (auditPackage.exports?.find(e => e.name.includes('Objectif'))?.count || 0) > 0 },
              { label: 'Preuves pédagogiques archivées', check: (auditPackage.exports?.find(e => e.name.includes('Justification'))?.count || 0) > 0 },
              { label: 'Feedbacks apprenants collectés', check: (auditPackage.exports?.find(e => e.name.includes('Feedback'))?.count || 0) > 0 },
              { label: 'Registre amélioration continue', check: (auditPackage.exports?.find(e => e.name.includes('Registre'))?.count || 0) >= 0 },
              { label: 'Attestations générées', check: (auditPackage.exports?.find(e => e.name.includes('Attestation'))?.count || 0) >= 0 }
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-green-100">
                {item.check ? (
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-amber-500" />
                )}
                <span className={`text-sm ${item.check ? 'text-green-700' : 'text-amber-700'}`}>
                  {item.label}
                </span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Structure dossier audit */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <FileText className="w-5 h-5 text-[#3B1E73]" />
            Structure recommandée du dossier audit
          </CardTitle>
          <p className="text-sm text-gray-500 mt-1">
            Organisation des fichiers pour présentation à l&apos;auditeur Qualiopi
          </p>
        </CardHeader>
        <CardContent>
          <pre className="bg-gray-900 text-green-400 p-5 rounded-xl text-sm overflow-x-auto font-mono">
{`📁 audit_qualiopi_${new Date().toISOString().split('T')[0]}/
│
├── 📁 01_positionnement/
│   ├── diagnostics_entree.csv      ← Indicateur 11
│   └── diagnostics_sortie.csv      ← Progression mesurée
│
├── 📁 02_preuves_acquisition/
│   ├── objectifs_valides.csv       ← Traçabilité Owen
│   └── justifications.csv          ← Preuves détaillées
│
├── 📁 03_amelioration_continue/
│   ├── feedbacks_apprenants.csv    ← Indicateur 32
│   └── registre_qualiopi.csv       ← Actions correctives
│
└── 📁 04_attestations/
    └── certificats_formation.pdf   ← Documents officiels`}
          </pre>
        </CardContent>
      </Card>
    </div>
  );
};

/**
 * Feedbacks Modules Tab
 * Affiche tous les feedbacks utilisateurs liés aux modules
 * Qualiopi Indicateur 32 - Amélioration Continue
 */
const FeedbacksTab = ({ data, onRefresh }) => {
  const [selectedModule, setSelectedModule] = useState('all');
  
  if (!data) return (
    <div className="text-center py-12 text-gray-500">
      Chargement des feedbacks...
    </div>
  );
  
  const { feedbacks, total, average_rating, categories_stats, modules_stats } = data;
  
  // Filtrer par module si sélectionné
  const filteredFeedbacks = selectedModule === 'all' 
    ? feedbacks 
    : feedbacks.filter(f => f.module_id === parseInt(selectedModule));

  // Convertir modules_stats en array pour affichage
  const modulesArray = Object.entries(modules_stats || {}).map(([id, stats]) => ({
    id: parseInt(id),
    ...stats
  })).sort((a, b) => b.count - a.count);

  // Catégories pour légende
  const categoryLabels = {
    contenu: '📚 Contenu',
    pedagogie: '🎓 Pédagogie',
    technique: '⚙️ Technique',
    general: '💬 Général'
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star 
        key={i} 
        className={`w-4 h-4 ${i < rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} 
      />
    ));
  };

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <MessageSquare className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#2B1854]">{total}</p>
                <p className="text-xs text-gray-500">Feedbacks Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Star className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#2B1854]">{average_rating || '-'}</p>
                <p className="text-xs text-gray-500">Note Moyenne</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <BookOpen className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#2B1854]">{modulesArray.length}</p>
                <p className="text-xs text-gray-500">Modules avec Feedback</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Shield className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-lg font-bold text-green-600">Indicateur 32</p>
                <p className="text-xs text-gray-500">Qualiopi</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter & Categories */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Filtre par Module */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <BookOpen className="w-4 h-4" />
              Filtrer par Module
            </CardTitle>
          </CardHeader>
          <CardContent>
            <select
              value={selectedModule}
              onChange={(e) => setSelectedModule(e.target.value)}
              className="w-full p-2 border rounded-lg text-sm bg-white"
            >
              <option value="all">Tous les modules ({total})</option>
              {modulesArray.map(mod => (
                <option key={mod.id} value={mod.id}>
                  Module {mod.id} - {mod.module_name} ({mod.count})
                </option>
              ))}
            </select>
          </CardContent>
        </Card>

        {/* Répartition par Catégorie */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Répartition par Catégorie
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {Object.entries(categories_stats || {}).map(([cat, count]) => (
                <Badge key={cat} variant="outline" className="gap-1">
                  {categoryLabels[cat] || cat}
                  <span className="bg-[#3B1E73] text-white px-1.5 py-0.5 rounded text-xs ml-1">
                    {count}
                  </span>
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Liste des Feedbacks */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-[#3B1E73]" />
              Feedbacks Utilisateurs ({filteredFeedbacks.length})
            </span>
            <Button variant="outline" size="sm" onClick={onRefresh} className="gap-2">
              <RefreshCw className="w-4 h-4" />
              Actualiser
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredFeedbacks.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Aucun feedback pour ce filtre</p>
            </div>
          ) : (
            <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
              {filteredFeedbacks.map((feedback, idx) => (
                <motion.div
                  key={feedback.feedback_id || idx}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.03 }}
                  className="p-4 bg-gradient-to-r from-gray-50 to-white border rounded-lg hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      {/* Header */}
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <Badge className="bg-[#3B1E73] text-white">
                          Module {feedback.module_id}
                        </Badge>
                        <span className="text-xs text-gray-500">
                          {feedback.module_name}
                        </span>
                        <Badge variant="outline" className="text-xs">
                          {categoryLabels[feedback.category] || feedback.category}
                        </Badge>
                      </div>
                      
                      {/* Stars */}
                      <div className="flex items-center gap-1 mb-2">
                        {renderStars(feedback.rating)}
                        <span className="text-sm text-gray-500 ml-2">
                          ({feedback.rating}/5)
                        </span>
                      </div>
                      
                      {/* Feedback Text */}
                      <p className="text-sm text-gray-700 leading-relaxed italic">
                        &ldquo;{feedback.feedback_text}&rdquo;
                      </p>
                      
                      {/* Footer */}
                      <div className="flex items-center gap-4 mt-3 text-xs text-gray-400">
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {feedback.user_id?.split('@')[0] || 'Anonyme'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(feedback.created_at).toLocaleDateString('fr-FR', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPedagogicalGovernance;
